import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * O kit visual bento — a "linguagem 1b" aprovada no redesign de 2026-08
 * (inicio-unico-v2.html da sessão 26dd5c48): rótulo 12.5px, número 28px com
 * tracking -0.03em, apoio 11.5px. Em 2026-10 a moldura passou para a
 * estrutura "fintech" (conceito A): raio 8px, borda sólida --border e a
 * sombra em duas camadas (shadow-cartao), nas cores da North.
 *
 * Mora FORA do dashboard porque é a linguagem de TODAS as telas (decisão de
 * 2026-08-25): quem redesenha uma página importa daqui em vez de recriar a
 * moldura. Mexer aqui é mexer no app inteiro.
 */

/** O card base. `col` é a largura na grade de 4 colunas do Início. */
export const CardBento = ({
  col = 1,
  className = '',
  id,
  children,
}: {
  col?: 1 | 2 | 3 | 4;
  className?: string;
  /** Âncora, para outro pedaço da mesma tela conseguir rolar até este card. */
  id?: string;
  children: ReactNode;
}) => {
  const span =
    col === 4
      ? 'md:col-span-2 xl:col-span-4'
      : col === 3
        ? 'md:col-span-2 xl:col-span-3'
        : col === 2
          ? 'md:col-span-2'
          : '';
  return (
    <div
      id={id}
      className={`relative cartao bg-card px-5 py-[18px] ${span} ${className}`}
    >
      {children}
    </div>
  );
};

/** Rótulo pequeno do card ("Produção de agosto"). */
export const Rotulo = ({ children }: { children: ReactNode }) => (
  <p className="text-[12.5px] font-semibold text-[#6b7480] dark:text-muted-foreground">
    {children}
  </p>
);

/** O número grande dos cards pequenos: 28px, como no mock. */
export const ValorGrande = ({
  children,
  unidade,
}: {
  children: ReactNode;
  unidade?: string;
}) => (
  <p className="mt-2 text-[28px] font-bold leading-none tracking-[-0.03em] tabular-nums">
    {children}
    {unidade ? (
      <span className="text-base font-normal text-muted-foreground">{unidade}</span>
    ) : null}
  </p>
);

/** Linha de apoio embaixo do número. `tom` colore comparações. */
export const Sub = ({
  tom,
  className = '',
  children,
}: {
  tom?: 'bom' | 'ruim';
  className?: string;
  children: ReactNode;
}) => (
  <p
    className={`mt-2 text-[11.5px] ${
      tom === 'bom'
        ? 'font-semibold text-[#166534]'
        : tom === 'ruim'
          ? 'font-semibold text-[#b45309]'
          : 'text-[#6b7480] dark:text-muted-foreground'
    } ${className}`}
  >
    {children}
  </p>
);

/**
 * O selo de escopo no canto do card ("gestão", "super admin").
 *
 * Existe pro gerente que compartilha a tela com um vendedor entender por que
 * o outro não vê aquele card — o selo diz de quem o card é, sem tooltip.
 */
export const SeloEscopo = ({ children }: { children: ReactNode }) => (
  <span className="absolute right-3.5 top-3.5 rounded-full border border-dashed border-[rgba(53,70,96,0.28)] bg-[rgba(53,70,96,0.03)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
    {children}
  </span>
);

/** O link discreto de rodapé ("Ver todos os atrasos"). */
export const VerMais = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="mt-3 inline-flex cursor-pointer items-center gap-1.5 text-[13px] font-semibold text-primary hover:text-primary/80"
  >
    {children}
    <ChevronRight className="h-3.5 w-3.5" />
  </button>
);

/** Cabeçalho de card de lista: título 14px + apoio à direita. */
export const CabecalhoLista = ({
  titulo,
  extra,
}: {
  titulo: string;
  extra?: ReactNode;
}) => (
  <div className="flex items-baseline justify-between gap-3">
    <h3 className="text-sm font-semibold tracking-[-0.005em]">{titulo}</h3>
    {extra ? (
      <span className="text-xs tabular-nums text-muted-foreground">{extra}</span>
    ) : null}
  </div>
);

/**
 * Cabeçalho de página do bento — o par do cabeçalho do Início pras demais
 * telas: título 22px com tracking apertado, descrição 13px, ações à direita.
 * (No mock é o topo do frame 1b: "Início" + "Consórcio · toda a equipe ·
 * agosto de 2026".)
 */
export const CabecalhoPagina = ({
  titulo,
  descricao,
  acoes,
  aoLadoDoTitulo,
}: {
  titulo: ReactNode;
  descricao?: ReactNode;
  acoes?: ReactNode;
  /** Colado no título, fora do <h1> (o olhinho dos valores). */
  aoLadoDoTitulo?: ReactNode;
}) => (
  // Premium North (design-system/north/MASTER.md): o degradê marinho do estudo
  // e do lance livre, com o anel dourado no canto. Os botões de ação ganham o
  // tom de cima do degradê pelo seletor de filho, sem mudar quem os desenha.
  <div className="cabecalho-premium relative flex flex-wrap items-center justify-between gap-x-6 gap-y-3 overflow-hidden rounded-[14px] bg-premium px-5 py-5 text-white sm:px-6">
    <span
      aria-hidden
      className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full border border-ouro/35 shadow-[0_0_0_40px_rgba(196,173,142,0.05)]"
    />
    <div className="relative min-w-0">
      {aoLadoDoTitulo ? (
        <div className="flex items-center gap-1.5">
          <h1 className="text-[24px] font-bold leading-tight tracking-[-0.02em] text-white sm:text-[26px]">{titulo}</h1>
          {aoLadoDoTitulo}
        </div>
      ) : (
        <h1 className="text-[24px] font-bold leading-tight tracking-[-0.02em] text-white sm:text-[26px]">{titulo}</h1>
      )}
      {descricao ? <p className="mt-1 text-[13px] text-white/72">{descricao}</p> : null}
    </div>
    {acoes ? <div className="relative flex min-w-0 max-w-full flex-wrap items-center gap-2">{acoes}</div> : null}
  </div>
);

/**
 * As abas segmentadas do mock (trilho cinza, aba ativa em pastilha branca) —
 * classes prontas pra `TabsList`/`TabsTrigger` do shadcn, no lugar dos estilos
 * locais que cada seção inventou (Campanhas tinha os seus; Insights importava
 * de lá).
 */
// No celular as abas que não cabem rolam dentro do trilho (max-w-full +
// overflow-x-auto), sem quebrar o nome em duas linhas nem empurrar a página
// para o lado. Na tela larga nada muda: o trilho continua do tamanho das abas.
export const abasBentoLista =
  'inline-flex h-auto max-w-full justify-start gap-5 overflow-x-auto rounded-none border-b border-border bg-transparent p-0 [scrollbar-width:none]';
export const abasBentoGatilho =
  'shrink-0 cursor-pointer whitespace-nowrap rounded-none border-0 border-b-2 border-transparent bg-transparent px-0.5 pb-2.5 pt-1 text-[13px] font-medium text-muted-foreground shadow-none ' +
  'data-[state=active]:border-ouro data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-marinho data-[state=active]:shadow-none';

/**
 * Receita de tabela bento, pra aplicar nos Table do shadcn sem trocar o
 * componente: cabeçalho 12px #8a929c sem caixa alta, linhas com o divisor
 * azulado fino do desenho.
 */
export const tabelaBentoHead =
  'text-xs font-semibold text-muted-foreground dark:text-muted-foreground';
export const tabelaBentoLinha = 'border-b border-[rgba(53,70,96,0.09)]';

/**
 * Cabeçalho de tabela na cor da marca (#354660, o `--primary`).
 *
 * Usa o token semântico em vez do hexadecimal para acompanhar o tema: no modo
 * escuro o par primary/primary-foreground continua legível, um hex fixo não.
 * O seletor de filho pinta as células sem precisar repetir a classe em cada
 * <th> — e é o que permite a MESMA receita servir para uma tabela do shadcn e
 * para um cabeçalho montado com flex (a lista de contatos).
 */
// Fica azul no redesign Premium North: pedido anterior do Rick para Contatos,
// Clientes e Inadimplentes (cabecalhoTabelaNorth.test.tsx guarda isso).
export const cabecalhoTabelaNorth =
  'bg-primary text-primary-foreground [&>th]:text-primary-foreground [&>div]:text-primary-foreground';

/**
 * Card pequeno de resumo pra qualquer tela: rótulo + número + detalhe, com o
 * quadradinho do ícone à direita. É o mesmo desenho dos contadores do Início,
 * em 24px (as telas comuns têm grades mais densas que o dashboard).
 */
export const CartaoStat = ({
  rotulo,
  valor,
  detalhe,
  icone,
  tom,
}: {
  rotulo: string;
  valor: ReactNode;
  detalhe?: ReactNode;
  icone?: ReactNode;
  tom?: 'danger' | 'bom';
}) => (
  <div className="cartao border-t-[3px] border-t-ouro bg-card px-4 py-3.5">
    <div className="flex items-center justify-between gap-2">
      <p className="truncate text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
        {rotulo}
      </p>
      {icone ? (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[rgba(53,70,96,0.07)] text-primary">
          {icone}
        </span>
      ) : null}
    </div>
    <p
      className={`mt-1.5 truncate text-[23px] font-bold leading-none tracking-[-0.02em] tabular-nums ${
        tom === 'danger' ? 'text-[#b91c1c]' : tom === 'bom' ? 'text-[#166534]' : 'text-marinho'
      }`}
    >
      {valor}
    </p>
    {detalhe ? (
      <p className="mt-1.5 truncate text-[11.5px] text-[#6b7480] dark:text-muted-foreground">
        {detalhe}
      </p>
    ) : null}
  </div>
);
